import {
    GetPaginatedSubjectDidsDocument,
    useRevokeSubjectDidMutation,
} from "@/src/graphql/generated/api/graphql";
import { memo, useEffect, useState } from "react";
import { styled } from "./SubjectDIDsOverviewTemplate.styles";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "@/src/components/organisms/ConfirmDialog/ConfirmDialog";
import { useNavigate } from "@tanstack/react-router";
import { Alert, Snackbar } from "@mui/material";
import SubjectDidsTable from "../../organisms/SubjectDidsTable/SubjectDidsTable";
import { usePaginatedSubjectDids } from "@/src/lib/usePaginatedSubjectDids/usePaginatedSubjectDids";
import { usePermissions } from "@/src/lib/usePermissions/usePermissions";

type SubjectDid = {
    id: string;
    name: string;
    zinlDid: string;
};

const SubjectDIDsOverviewTemplate = () => {
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(15);
    const { hasPermission } = usePermissions();

    const { loading, subjectDids, count } = usePaginatedSubjectDids(
        page,
        pageSize
    );

    const [toDelete, setToDelete] = useState<SubjectDid | null>(null);
    const [confirmedDelete, setConfirmedDelete] = useState<SubjectDid | null>(
        null
    );

    const [snackbar, setSnackbar] = useState<{
        key: number;
        open: boolean;
        message: string;
        severity: "success" | "error";
    }>({
        key: 0,
        open: false,
        message: "",
        severity: "success",
    });

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const navigate = useNavigate();

    const { t } = useTranslation("common", {
        keyPrefix: "subject-dids-overview",
    });

    const [deleteSubjectDid] = useRevokeSubjectDidMutation({
        variables: {
            id: confirmedDelete?.id ?? "",
        },
        refetchQueries: [GetPaginatedSubjectDidsDocument],
        onCompleted: () => {
            setSnackbar((prev) => ({
                ...prev,
                key: prev.key + 1,
                open: true,
                message: t("deleted-success"),
                severity: "success",
            }));
            setConfirmedDelete(null);
            setPage(0);
        },
        onError: () => {
            setSnackbar((prev) => ({
                ...prev,
                key: prev.key + 1,
                open: true,
                message: t("could-not-be-deleted"),
                severity: "error",
            }));
            setConfirmedDelete(null);
        },
    });

    useEffect(() => {
        if (confirmedDelete) {
            deleteSubjectDid().finally();
        }
    }, [confirmedDelete]);

    const handleDeleteClick = (id: string) => {
        const item = subjectDids?.find((item) => item.id == id) ?? null;

        setToDelete(item);
    };

    const handleDeleteConfirmClose = ({
        confirmed,
    }: {
        confirmed?: boolean | null;
    }) => {
        if (confirmed) {
            setConfirmedDelete(toDelete);
        }

        setToDelete(null);
    };

    const handleItemClick = (id: string) => {
        void navigate({ to: `/admin/subject-dids/${id}` });
    };

    const handlePageChange = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setPageSize(parseInt(event.target.value));
        setPage(0);
    };

    const onItemClick = hasPermission("SUBJECT_DIDS_UPDATE")
        ? handleItemClick
        : undefined;

    return (
        <>
            <styled.Container>
                <styled.Header
                    title={t("title")}
                    button={{
                        to: "new",
                        label: t("new"),
                    }}
                />
                <styled.Main>
                    <SubjectDidsTable
                        subjectDids={subjectDids || []}
                        loading={loading}
                        onDeleteClick={handleDeleteClick}
                        onItemClick={onItemClick}
                        page={page}
                        rowsPerPage={pageSize}
                        totalCount={count || 0}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                    />
                </styled.Main>
            </styled.Container>
            <ConfirmDialog
                open={Boolean(toDelete !== null)}
                indicators={[
                    { id: toDelete?.id ?? "", name: toDelete?.name ?? "" },
                ]}
                onClose={handleDeleteConfirmClose}
                translationPrefix="confirm-dialogs.subject-dids"
            />
            <Snackbar
                key={snackbar.key}
                open={snackbar.open}
                onClose={handleCloseSnackbar}
                autoHideDuration={snackbar.severity === "success" ? 3000 : null}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={handleCloseSnackbar}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default memo(SubjectDIDsOverviewTemplate);
